<script setup lang="ts">
import type { TenantLandingConfig } from '~/types/types'

/**
 * About block.
 *  - side:  text + image side-by-side (Classic / Split / Bold)
 *  - stack: image above narrow text column (Editorial / Minimal)
 */
withDefaults(defineProps<{
  landing: TenantLandingConfig
  variant?: 'side' | 'stack'
  displayClass?: string
}>(), { variant: 'side', displayClass: '' })
</script>

<template>
  <section v-if="landing.about_title || landing.about_description">
    <div
      v-if="variant === 'side'"
      class="mx-auto max-w-6xl px-6 py-20"
    >
      <div class="grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <h2
            v-if="landing.about_title"
            class="text-3xl tracking-tight"
            :class="displayClass"
          >
            {{ landing.about_title }}
          </h2>
          <p
            v-if="landing.about_description"
            class="mt-4 text-base ui-text-subtle whitespace-pre-line leading-relaxed"
          >
            {{ landing.about_description }}
          </p>
        </div>
        <div
          v-if="landing.about_image_url"
          class="rounded-2xl overflow-hidden aspect-[4/3]"
        >
          <img
            :src="landing.about_image_url"
            :alt="landing.about_title || ''"
            class="w-full h-full object-cover"
          >
        </div>
      </div>
    </div>

    <div
      v-else
      class="mx-auto max-w-3xl px-6 py-16"
    >
      <div
        v-if="landing.about_image_url"
        class="overflow-hidden rounded-xl aspect-[16/9] mb-8"
      >
        <img
          :src="landing.about_image_url"
          :alt="landing.about_title || ''"
          class="w-full h-full object-cover"
        >
      </div>
      <h2
        v-if="landing.about_title"
        class="text-2xl sm:text-3xl tracking-tight"
        :class="displayClass"
      >
        {{ landing.about_title }}
      </h2>
      <p
        v-if="landing.about_description"
        class="mt-4 text-base ui-text-subtle whitespace-pre-line leading-relaxed"
      >
        {{ landing.about_description }}
      </p>
    </div>
  </section>
</template>
